import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useTranslation } from 'react-i18next';
import config from '../../config';
import useFetchUserProfile from '../../hooks/useFetchAccount';
import useGitAdminApi from '../../hooks/useGitAdminApi';
import { GitAdminCoverage, GitAdminIssue, GitAdminRepository, GitAdminTreeResponse, GitRefreshRequest, GitRefreshSummary } from '../../interfaces';
import GitAdminHierarchy from './GitAdminHierarchy';
import GitAdminHeaderCard from './GitAdminHeaderCard';
import GitAdminIssuesCards from './GitAdminIssuesCards';
import GitAdminAppsTimelineCard from './GitAdminAppsTimelineCard';
import GitAdminUserWorkloadCard from './GitAdminUserWorkloadCard';
import useGitAdminTimeline from './hooks/useGitAdminTimeline';

const LIST_PAGE_SIZE = 50;

type IssueMergeRequestMeta = {
  mergeRequestUrl: string;
  pipelineStatus?: string | null;
};

type GitLabCurrentUser = {
  id?: number;
  username?: string;
  name?: string;
};

const normalizeText = (value?: string | null): string => (value ?? '').trim().toLowerCase();

const buildIssueKey = (repositoryId: number, issueTitle?: string | null, issueOwner?: string | null): string =>
  `${repositoryId}::${normalizeText(issueTitle)}::${normalizeText(issueOwner)}`;

const buildIssueKeyWithoutOwner = (repositoryId: number, issueTitle?: string | null): string =>
  `${repositoryId}::${normalizeText(issueTitle)}`;

const renderMergeRequestPill = (meta?: IssueMergeRequestMeta): React.ReactNode => {
  if (!meta?.mergeRequestUrl) {
    return <span className="text-muted">-</span>;
  }

  const normalizedStatus = normalizeText(meta.pipelineStatus);
  const statusClassName = normalizedStatus === 'success'
    ? 'gitadmin-status-dot-success'
    : normalizedStatus === 'failed'
      ? 'gitadmin-status-dot-failed'
      : 'gitadmin-status-dot-default';

  return (
    <a href={meta.mergeRequestUrl} target="_blank" rel="noopener noreferrer" title="Open merge request" aria-label="Open merge request">
      <span className={`gitadmin-status-dot ${statusClassName}`} />
    </a>
  );
};

const formatDuration = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
};

const formatHoursAxisLabel = (seconds: number): string => {
  const hours = Math.max(0, seconds) / 3600;
  if (hours >= 10 || Number.isInteger(hours)) {
    return `${hours.toFixed(0)}h`;
  }

  return `${hours.toFixed(1)}h`;
};

const renderTimeProgress = (timeSpentSeconds?: number | null, timeEstimateSeconds?: number | null): React.ReactNode => {
  const spent = Math.max(0, timeSpentSeconds ?? 0);
  const estimate = Math.max(0, timeEstimateSeconds ?? 0);

  if (estimate <= 0) {
    return <span className="text-muted">N/A</span>;
  }

  const ratio = Math.min(100, (spent / estimate) * 100);
  const barVariant = spent > estimate ? 'danger' : 'success';

  return (
    <div className="gitadmin-time-progress">
      <ProgressBar now={ratio} variant={barVariant} label={`${Math.round(ratio)}%`} />
      <div className="small text-muted mt-1">
        {formatDuration(spent)} / {formatDuration(estimate)}
      </div>
    </div>
  );
};

const formatTimelineTick = (timestamp: number, minTs: number, maxTs: number): string => {
  const value = new Date(timestamp);
  const sameDay = new Date(minTs).toDateString() === new Date(maxTs).toDateString();

  if (sameDay) {
    return value.toLocaleString();
  }

  return value.toLocaleDateString();
};

const GitAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { profile, error: profileError } = useFetchUserProfile();
  const {
    loading,
    error,
    getRepositories,
    getCoverages,
    getIssues,
    getTree,
    updateIssue,
    refreshAll,
    getRefreshStatus,
    getIssueTimeEvents,
    refreshIssueApp,
    getWorkloadByUser
  } = useGitAdminApi();

  const [tree, setTree] = useState<GitAdminTreeResponse | null>(null);
  const [repositories, setRepositories] = useState<GitAdminRepository[]>([]);
  const [coverages, setCoverages] = useState<GitAdminCoverage[]>([]);
  const [issues, setIssues] = useState<GitAdminIssue[]>([]);
  const [gitLabCurrentUser, setGitLabCurrentUser] = useState<GitLabCurrentUser | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const [issueStateFilter, setIssueStateFilter] = useState<'opened' | 'closed' | 'all'>('opened');
  const [showUnassignedIssues, setShowUnassignedIssues] = useState(false);
  const [showAppsTimeline, setShowAppsTimeline] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null);
  const [refreshCurrentAction, setRefreshCurrentAction] = useState<string | null>(null);
  const [refreshPendingActions, setRefreshPendingActions] = useState<number>(0);
  const [refreshSummary, setRefreshSummary] = useState<GitRefreshSummary | null>(null);
  const [refreshActionError, setRefreshActionError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState(0);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [gitLabApiBaseUrl, setGitLabApiBaseUrl] = useState(config.gitApiBaseUrl);
  const [treeLoading, setTreeLoading] = useState(false);
  const gitToken = profile?.gitToken ?? '';

  const fetchGitLabCurrentUser = useCallback(async (): Promise<GitLabCurrentUser | null> => {
    if (!gitToken) {
      return null;
    }

    try {
      const response = await fetch(`${gitLabApiBaseUrl}/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${gitToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const currentUser = await response.json() as GitLabCurrentUser;
      return currentUser;
    } catch {
      return null;
    }
  }, [gitLabApiBaseUrl, gitToken]);

  const fetchPagedList = useCallback(async <T,>(
    fetchPageResponse: (index: number, nbItems: number) => Promise<{ result: T[]; total: number }>,
    onFirstPage?: (items: T[]) => void
  ): Promise<T[]> => {
    const firstPage = await fetchPageResponse(0, LIST_PAGE_SIZE);
    const firstPageItems = firstPage.result ?? [];
    onFirstPage?.(firstPageItems);

    if (firstPageItems.length < LIST_PAGE_SIZE) {
      return firstPageItems;
    }

    const allItems: T[] = [...firstPageItems];
    let offset = LIST_PAGE_SIZE;

    while (true) {
      const response = await fetchPageResponse(offset, LIST_PAGE_SIZE);
      const pageItems = response.result ?? [];

      if (pageItems.length === 0) {
        break;
      }

      allItems.push(...pageItems);

      if (pageItems.length < LIST_PAGE_SIZE) {
        break;
      }

      offset += LIST_PAGE_SIZE;
    }

    return allItems;
  }, []);

  const loadData = useCallback(async (_request: GitRefreshRequest) => {
    let completedRequests = 0;
    const requestCount = 3;
    const markProgress = () => {
      completedRequests += 1;
      const progressChunk = Math.floor(90 / requestCount);
      setLoadProgress(Math.min(95, 5 + completedRequests * progressChunk));
    };

    setLoadProgress(5);

    const repositoriesPromise = getRepositories().then((repositoryResponse) => {
      setRepositories(repositoryResponse.result ?? []);
      markProgress();
    });

    const coveragesPromise = fetchPagedList(
      (index, nbItems) => getCoverages({ index, nbItems, descOrder: false }),
      (firstPageItems) => setCoverages(firstPageItems)
    ).then((allCoverages) => {
      setCoverages(allCoverages);
      markProgress();
    });

    const issuesPromise = fetchPagedList(
      (index, nbItems) => getIssues({ index, nbItems, descOrder: false }),
      (firstPageItems) => setIssues(firstPageItems)
    ).then((allIssues) => {
      setIssues(allIssues);
      markProgress();
    });

    await Promise.all([repositoriesPromise, coveragesPromise, issuesPromise]);
    setLoadProgress(100);

    // Keep 100% visible briefly to make progress completion readable.
    setTimeout(() => setLoadProgress(0), 350);
  }, [getRepositories, getCoverages, getIssues, fetchPagedList]);

  const loadTreeOnDemand = useCallback(async () => {
    if (treeLoading || tree) {
      return;
    }

    if (!gitToken || !gitLabApiBaseUrl?.trim()) {
      return;
    }

    setTreeLoading(true);
    try {
      const treeResponse = await getTree({
        gitLabApiBaseUrl,
        gitToken
      });
      setTree(treeResponse);
    } finally {
      setTreeLoading(false);
    }
  }, [treeLoading, tree, gitToken, gitLabApiBaseUrl, getTree]);

  useEffect(() => {
    if (!gitToken) {
      setGitLabCurrentUser(null);
      return;
    }

    loadData({
      gitLabApiBaseUrl,
      gitToken
    }).catch(() => undefined);
  }, [loadData, gitLabApiBaseUrl, gitToken]);

  useEffect(() => {
    if (!gitToken) {
      setGitLabCurrentUser(null);
      return;
    }

    fetchGitLabCurrentUser()
      .then(user => setGitLabCurrentUser(user))
      .catch(() => setGitLabCurrentUser(null));
  }, [fetchGitLabCurrentUser, gitToken]);

  const saveIssue = async (issue: GitAdminIssue) => {
    await updateIssue(issue);
    await loadData({ gitLabApiBaseUrl, gitToken });
  };

  const refreshIssuesForUser = async () => {
    if (!gitToken) {
      return;
    }

    const currentUser = await fetchGitLabCurrentUser();
    setGitLabCurrentUser(currentUser);
    await loadData({ gitLabApiBaseUrl, gitToken });
  };

  const forceRefresh = async () => {
    if (!gitLabApiBaseUrl?.trim()) {
      setRefreshActionError('URL GitLab API manquante.');
      return;
    }

    if (!gitToken) {
      setRefreshActionError('Token GitLab manquant dans le profil utilisateur.');
      return;
    }

    setRefreshActionError(null);

    try {
      const result = await refreshAll({
        gitLabApiBaseUrl,
        gitToken
      });
      setOperationId(result.operationId);
      setRefreshStatus(result.status);
      setRefreshCurrentAction('Queued');
      setRefreshPendingActions(0);
      setRefreshSummary(null);
      setRefreshProgress(10);
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : 'Impossible de lancer le refresh Git.';
      setRefreshActionError(message);
    }
  };

  useEffect(() => {
    if (!operationId || !gitToken) {
      return;
    }

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const status = await getRefreshStatus(operationId);
        if (cancelled) {
          return;
        }

        setRefreshStatus(status.status);
        setRefreshCurrentAction(status.currentAction ?? null);
        setRefreshSummary(status.summary ?? null);

        const totalActions = Math.max(0, status.totalActions ?? 0);
        const completedActions = Math.max(0, status.completedActions ?? 0);
        const pendingActions = Math.max(0, totalActions - completedActions);
        setRefreshPendingActions(pendingActions);

        if (totalActions > 0) {
          setRefreshProgress(Math.max(1, Math.min(100, Math.round((completedActions / totalActions) * 100))));
        } else if (status.status === 'queued') {
          setRefreshProgress(10);
        } else if (status.status === 'running') {
          setRefreshProgress(25);
        }

        if (status.status === 'completed') {
          setRefreshProgress(100);
          clearInterval(interval);
          await loadData({ gitLabApiBaseUrl, gitToken });
          setTimeout(() => setRefreshProgress(0), 500);
        }

        if (status.status === 'failed') {
          setRefreshProgress(100);
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [operationId, getRefreshStatus, loadData, gitLabApiBaseUrl, gitToken]);

  const repositoriesById = useMemo(
    () => new Map<number, GitAdminRepository>(repositories.map(repository => [repository.id, repository])),
    [repositories]
  );
  const repositoriesByGitlabProjectId = useMemo(
    () => new Map<number, GitAdminRepository>(repositories.map(repository => [repository.gitlabProjectId, repository])),
    [repositories]
  );

  const issueMergeRequestMetaByKey = new Map<string, IssueMergeRequestMeta>();
  const issueMergeRequestMetaByTitleKey = new Map<string, IssueMergeRequestMeta>();

  (tree?.projects ?? []).forEach(project => {
    const repository = repositoriesByGitlabProjectId.get(project.gitlabProjectId);
    if (!repository) {
      return;
    }

    project.branches.forEach(branch => {
      if (!branch.mergeRequestUrl || !branch.issueTitle) {
        return;
      }

      const meta: IssueMergeRequestMeta = {
        mergeRequestUrl: branch.mergeRequestUrl,
        pipelineStatus: branch.pipelineStatus
      };

      const keyWithOwner = buildIssueKey(repository.id, branch.issueTitle, branch.issueOwner);
      const keyWithoutOwner = buildIssueKeyWithoutOwner(repository.id, branch.issueTitle);

      if (!issueMergeRequestMetaByKey.has(keyWithOwner)) {
        issueMergeRequestMetaByKey.set(keyWithOwner, meta);
      }

      if (!issueMergeRequestMetaByTitleKey.has(keyWithoutOwner)) {
        issueMergeRequestMetaByTitleKey.set(keyWithoutOwner, meta);
      }
    });
  });

  const filteredIssues = issues.filter(issue => {
    if (!gitLabCurrentUser?.id) {
      if (issueStateFilter === 'all') {
        return true;
      }

      return (issue.state ?? 'opened') === issueStateFilter;
    }

    if (issue.gitUserId !== gitLabCurrentUser.id) {
      return false;
    }

    if (issueStateFilter === 'all') {
      return true;
    }

    return (issue.state ?? 'opened') === issueStateFilter;
  });

  const unassignedIssues = issues.filter(issue => !issue.gitUserId);

  const {
    appsScope,
    setAppsScope,
    selectedAppsIssueId,
    setSelectedAppsIssueId,
    appsIssues,
    selectedAppsIssue,
    appsTimeEventsLoading,
    appsTimeEventsError,
    appsIssueRefreshLoading,
    appsIssueRefreshMessage,
    appsTimelineDebug,
    appsTimelineGraph,
    relatedAppsIssues,
    refreshAppsTimeline,
    refreshSelectedAppsIssue
  } = useGitAdminTimeline({
    issues,
    setIssues,
    repositoriesById,
    gitToken,
    gitLabApiBaseUrl,
    showAppsTimeline,
    getIssueTimeEvents,
    refreshIssueApp,
    fetchPagedList,
    getIssues
  });

  if (loading && !tree && coverages.length === 0 && issues.length === 0) {
    return (
      <Container className="mt-4 d-flex justify-content-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <GitAdminHeaderCard
        gitLabApiBaseUrl={gitLabApiBaseUrl}
        setGitLabApiBaseUrl={setGitLabApiBaseUrl}
        gitToken={gitToken}
        loading={loading}
        profileError={profileError}
        refreshActionError={refreshActionError}
        operationId={operationId}
        refreshStatus={refreshStatus}
        refreshCurrentAction={refreshCurrentAction}
        refreshPendingActions={refreshPendingActions}
        refreshSummary={refreshSummary}
        refreshProgress={refreshProgress}
        onForceRefresh={forceRefresh}
        apiError={error}
      />

      {loadProgress > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <div className="small text-muted mb-2">{t('gitAdmin.loadingData')}</div>
            <ProgressBar now={loadProgress} animated={loadProgress < 100} label={`${loadProgress}%`} />
          </Card.Body>
        </Card>
      )}

      <Row>
        <Col lg={12}>
          <GitAdminIssuesCards
            issueStateFilter={issueStateFilter}
            setIssueStateFilter={setIssueStateFilter}
            showIssues={showIssues}
            setShowIssues={setShowIssues}
            filteredIssues={filteredIssues}
            refreshIssuesForUser={refreshIssuesForUser}
            repositoriesById={repositoriesById}
            buildIssueKey={buildIssueKey}
            buildIssueKeyWithoutOwner={buildIssueKeyWithoutOwner}
            issueMergeRequestMetaByKey={issueMergeRequestMetaByKey}
            issueMergeRequestMetaByTitleKey={issueMergeRequestMetaByTitleKey}
            renderMergeRequestPill={renderMergeRequestPill}
            renderTimeProgress={renderTimeProgress}
            setIssues={setIssues}
            saveIssue={saveIssue}
            showUnassignedIssues={showUnassignedIssues}
            setShowUnassignedIssues={setShowUnassignedIssues}
            unassignedIssues={unassignedIssues}
          />

          <GitAdminAppsTimelineCard
            showAppsTimeline={showAppsTimeline}
            setShowAppsTimeline={setShowAppsTimeline}
            appsScope={appsScope}
            setAppsScope={setAppsScope}
            selectedAppsIssueId={selectedAppsIssueId}
            setSelectedAppsIssueId={setSelectedAppsIssueId}
            appsIssues={appsIssues}
            selectedAppsIssue={selectedAppsIssue}
            appsIssueRefreshLoading={appsIssueRefreshLoading}
            refreshSelectedAppsIssue={refreshSelectedAppsIssue}
            appsTimeEventsLoading={appsTimeEventsLoading}
            refreshAppsTimeline={refreshAppsTimeline}
            appsTimeEventsError={appsTimeEventsError}
            appsIssueRefreshMessage={appsIssueRefreshMessage}
            appsTimelineGraph={appsTimelineGraph}
            formatDuration={formatDuration}
            relatedAppsIssues={relatedAppsIssues}
            repositoriesById={repositoriesById}
            formatHoursAxisLabel={formatHoursAxisLabel}
            formatTimelineTick={formatTimelineTick}
            appsTimelineDebug={appsTimelineDebug}
            renderTimeProgress={renderTimeProgress}
          />

          <GitAdminUserWorkloadCard
            issues={issues}
            fetchWorkloadByUser={getWorkloadByUser}
            triggerRefreshAll={refreshAll}
            getRefreshStatus={getRefreshStatus}
            formatDuration={formatDuration}
            gitLabApiBaseUrl={gitLabApiBaseUrl}
            gitToken={gitToken}
          />
        </Col>
      </Row>

      <GitAdminHierarchy tree={tree} loading={treeLoading} onExpand={loadTreeOnDemand} />

    </Container>
  );
};

export default GitAdminPage;
